import React, { useRef, useEffect } from 'react';
import { Box, Button, Typography, IconButton, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const PASSENGER_TYPES = [
  { key: 'adults', label: 'Adults', hint: '18-64' },
  { key: 'students', label: 'Students', hint: 'over 18' },
  { key: 'seniors', label: 'Seniors', hint: 'over 65' },
  { key: 'youths', label: 'Youths', hint: '12-17' },
  { key: 'children', label: 'Children', hint: '2-11' },
  { key: 'toddlers', label: 'Toddlers in own seat', hint: 'under 2' },
  { key: 'infants', label: 'Infants on lap', hint: 'under 2' },
];

function getTotalTravelers(counts) {
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

function getTravelerLabel(counts) {
  const total = getTotalTravelers(counts);
  if (total === 1 && counts.adults === 1) return '1 adult';
  return `${total} travelers`;
}

function PassengerDropdown({
  open,
  anchorEl,
  counts,
  setCounts,
  onClose,
  // Removed showCabinClass, cabinClass, setCabinClass
}) {
  // Validation
  const total = getTotalTravelers(counts);
  const errors = [];
  if (total < 1) errors.push('Searches need at least 1 traveler');
  if (total > 16) errors.push('Searches cannot have more than 16 travelers');
  if (counts.adults > 9) errors.push('Searches cannot have more than 9 adults');
  if (counts.adults === 0 && (counts.youths > 0 || counts.children > 0)) {
    errors.push('KAYAK cannot run searches for unaccompanied minors');
  }

  // Dropdown position
  const paperRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    function handleClick(event) {
      if (
        paperRef.current &&
        !paperRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClose && onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, anchorEl, onClose]);

  if (!open) return null;
  const rect = anchorEl?.getBoundingClientRect?.() || { left: 0, top: 0, width: 0, height: 0 };
  const style = {
    position: 'absolute',
    top: 30,
    left: 90,
    zIndex: 2000,
    borderRadius: 2,
    boxShadow: '0 8px 32px rgba(60,60,60,0.12)',
    minWidth: 340,
    background: '#fff',
    border: 'none',
    padding: 0,
  };

  // Handlers
  const handleChange = (key, delta) => {
    const next = { ...counts, [key]: Math.max(0, counts[key] + delta) };
    if (getTotalTravelers(next) === 0) next.adults = 1;
    setCounts(next);
  };

  return (
    <Paper sx={style} ref={paperRef}>
      <Box sx={{ p: 3, pb: 2 }}>
        {PASSENGER_TYPES.map((type) => (
          <Box key={type.key} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 14, color: '#222', fontWeight: 500 }}>
                {type.label}
                {type.hint && (
                  <Typography component="span" sx={{ fontSize: 14, color: '#888', fontWeight: 400, ml: 1 }}>
                    {type.hint}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleChange(type.key, -1)}
                disabled={counts[type.key] === 0 || (type.key === 'adults' && counts.adults === 1 && total === 1)}
                sx={{
                  width: 20,
                  height: 20,
                  border: '1px solid #ddd',
                  borderRadius: '2px',
                  color: counts[type.key] === 0 ? '#ccc' : '#333',
                  backgroundColor: '#fff',
                  '&:hover:not(:disabled)': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <RemoveIcon sx={{ fontSize: '16px' }}/>
              </IconButton>
              <Typography sx={{ minWidth: 18, textAlign: 'center', fontWeight: 500, fontSize: 17, color: '#222' }}>
                {counts[type.key]}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleChange(type.key, 1)}
                disabled={
                  (type.key === 'adults' && counts.adults >= 9) ||
                  (total >= 16)
                }
                sx={{
                  width: 20,
                  height: 20,
                  border: '1px solid #ddd',
                  borderRadius: '2px',
                  color: (type.key === 'adults' && counts.adults >= 9) || total >= 16 ? '#ccc' : '#333',
                  backgroundColor: '#fff',
                  '&:hover:not(:disabled)': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: '16px' }}/>
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
      {/* Removed cabin class section */}
      {errors.length > 0 && (
        <Box sx={{ px: 3, pb: 2 }}>
          {errors.map((err, i) => (
            <Typography key={i} sx={{ color: '#e53935', fontSize: 15, fontWeight: 500, mt: 0.5, display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: 18, marginRight: 6 }}>⚠️</span> {err}
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default PassengerDropdown;
export { getTravelerLabel }; 